import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  DirectionsRun as RunIcon,
  Groups as TeamsIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/userService';

const WorkflowSettings: React.FC = () => {
  const { user, refreshCurrentUser } = useAuth();
  const [enabled, setEnabled] = useState(() => !!user?.useAthleteWorkflow);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    if (user?.useAthleteWorkflow !== undefined) {
      setEnabled(user.useAthleteWorkflow);
    }
  }, [user?.useAthleteWorkflow]);

  // Only show this component for coaches and head coaches
  if (!user || (user.role !== 'coach' && user.role !== 'headCoach')) {
    return null;
  }

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    if (!user) return;

    setLoading(true);
    setMessage(null);

    try {
      // Update user document in Firestore
      await userService.updateUser(user.id, {
        useAthleteWorkflow: newValue
      });

      setEnabled(newValue);
      await refreshCurrentUser(); // Keep auth in sync so returning to Settings shows correct state
      setMessage({
        type: 'success',
        text: newValue 
          ? '✅ Athlete workflow enabled! You\'ll see health metrics and activity entry on login. Refresh the page to apply changes.'
          : '✅ Coach workflow enabled! You\'ll go to team management on login. Refresh the page to apply changes.'
      });

      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      console.error('Error updating workflow preference:', error);
      setMessage({
        type: 'error',
        text: 'Failed to update workflow preference. Please try again.'
      });
      // Revert toggle on error
      setEnabled(!newValue);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {enabled ? (
            <RunIcon sx={{ mr: 1, color: 'primary.main' }} />
          ) : (
            <TeamsIcon sx={{ mr: 1, color: 'text.secondary' }} />
          )}
          <Typography variant="h6">Workflow Preference</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose how you want to interact with the app when you sign in.
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggle}
              disabled={loading}
            />
          }
          label={
            <Box>
              <Typography variant="body1">
                {enabled ? 'Athlete Workflow' : 'Coach Workflow'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {enabled 
                  ? 'Follow the same workflow as athletes: health metrics → activity → summary → dashboard'
                  : 'Go directly to team management page (default coach workflow)'}
              </Typography>
            </Box>
          }
        />

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Updating preference...
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>How it works:</strong><br />
            {enabled ? (
              <>
                • On login, you'll see health metrics if not filled out yet<br />
                • After health metrics, you'll enter your activity<br />
                • Then see activity summary and points earned<br />
                • Finally land on your dashboard<br />
                • Same experience as your athletes!
              </>
            ) : (
              <>
                • On login, you'll go directly to team management<br />
                • Manage athletes, view analytics, and team settings<br />
                • Access your own activities via "My Daily Log" in profile menu
              </>
            )}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default WorkflowSettings;

